import {
  ActionIcon,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useMemo, useState } from 'react';
import type { Practice, Role, User } from '@/pages/users/types/rbac';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import {
  formatSupervisionStartedAt,
  practitionerDisplayName,
  type PractitionerSupervisionOption,
  type SupervisionTableRow,
} from '@/lib/supervision/supervisionDisplay';
import {
  updateProviderSupervisor,
  updateRequiresCosignature,
  updateSupervisorSupervisees,
} from '@/lib/supervision/supervisionService';
import { useSupervisionCardData } from './useSupervisionCardData';

export interface SupervisionCardProps {
  practitionerId: string;
  practiceId?: string;
  practices: Practice[];
  allUsers: User[];
  /** Bumped by the parent after a role write so supervision links re-read. */
  refreshToken?: number;
  /** When true, supervision is read-only (deactivated account). */
  isInactive?: boolean;
  /** Parent-held roles — preferred over profile extensions for section visibility. */
  heldRoles?: Role[];
}

type AddModalMode = 'supervisor' | 'supervisee' | null;
type RemoveTarget = { mode: 'supervisor' | 'supervisee'; row: SupervisionTableRow } | null;

function buildColumns(
  canEdit: boolean,
  onRemove: (row: SupervisionTableRow) => void,
): UnifyTableColumn<SupervisionTableRow>[] {
  const columns: UnifyTableColumn<SupervisionTableRow>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <Text size="sm" fw={500}>
          {row.name}
        </Text>
      ),
    },
    {
      key: 'credentials',
      label: 'License',
      render: (row) => <Text size="sm">{row.credentials ?? '—'}</Text>,
    },
    {
      key: 'practiceName',
      label: 'Organization',
      render: (row) => <Text size="sm">{row.practiceName ?? '—'}</Text>,
    },
    {
      key: 'startedAt',
      label: 'Supervision started',
      render: (row) => <Text size="sm">{formatSupervisionStartedAt(row.startedAt)}</Text>,
    },
  ];

  if (canEdit) {
    columns.push({
      key: 'actions',
      label: '',
      align: 'right',
      pinRight: true,
      sortable: false,
      width: 56,
      render: (row) => (
        <ActionIcon
          variant="subtle"
          color="red"
          aria-label={`Remove ${row.name}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(row);
          }}
        >
          <IconTrash size={16} />
        </ActionIcon>
      ),
    });
  }

  return columns;
}

function SupervisionSection({
  title,
  rows,
  canEdit,
  emptyMessage,
  addLabel,
  onAdd,
  onRemove,
}: {
  title: string;
  rows: SupervisionTableRow[];
  canEdit: boolean;
  emptyMessage: string;
  addLabel: string;
  onAdd: () => void;
  onRemove: (row: SupervisionTableRow) => void;
}): JSX.Element {
  const columns = useMemo(() => buildColumns(canEdit, onRemove), [canEdit, onRemove]);

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={600}>
          {title}
        </Text>
        {canEdit && (
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={onAdd}>
            {addLabel}
          </Button>
        )}
      </Group>

      {rows.length > 0 ? (
        <UnifyTable
          withPaper={false}
          sortable={false}
          data={rows}
          columns={columns}
          getRowKey={(row) => row.id}
        />
      ) : (
        <Text size="sm" c="gray.6">
          {emptyMessage}
        </Text>
      )}
    </Stack>
  );
}

export function SupervisionCard({
  practitionerId,
  practiceId,
  practices,
  allUsers,
  refreshToken = 0,
  isInactive = false,
  heldRoles,
}: SupervisionCardProps): JSX.Element | null {
  const medplum = useMedplum();
  const data = useSupervisionCardData(
    practitionerId,
    practiceId,
    practices,
    allUsers,
    refreshToken,
  );

  const [addModalMode, setAddModalMode] = useState<AddModalMode>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget>(null);
  const [saving, setSaving] = useState(false);

  const clinicalRoles = heldRoles ?? data.viewedRoles;
  const hasSupervisorRole = clinicalRoles.includes('supervisor');
  const hasProviderRole = clinicalRoles.includes('provider');
  const showSupervisorSection =
    hasSupervisorRole || (isInactive && data.superviseeRows.length > 0);
  const showProviderSection =
    (hasProviderRole && !hasSupervisorRole) || (isInactive && !showSupervisorSection);
  const canEdit = data.canEdit && !isInactive;

  if (!showProviderSection && !showSupervisorSection) {
    return null;
  }

  const addCandidates: PractitionerSupervisionOption[] =
    addModalMode === 'supervisor'
      ? data.supervisorCandidates
      : addModalMode === 'supervisee'
        ? data.superviseeCandidates.filter((c) => !data.superviseeIds.includes(c.value))
        : [];

  const openAddModal = (mode: AddModalMode): void => {
    setAddModalMode(mode);
    setSelectedCandidateId(null);
  };

  const closeAddModal = (): void => {
    setAddModalMode(null);
    setSelectedCandidateId(null);
  };

  const handleAddConfirm = async (): Promise<void> => {
    if (!selectedCandidateId || !addModalMode) return;
    setSaving(true);
    try {
      if (addModalMode === 'supervisor') {
        await updateProviderSupervisor(medplum, practitionerId, selectedCandidateId);
      } else {
        await updateSupervisorSupervisees(medplum, practitionerId, [
          ...data.superviseeIds,
          selectedCandidateId,
        ]);
      }
      data.refresh();
      closeAddModal();
      notifications.show({
        color: 'green',
        title: 'Supervision updated',
        message:
          addModalMode === 'supervisor'
            ? 'Supervisor assigned.'
            : 'Supervisee added.',
      });
    } catch {
      notifications.show({
        color: 'red',
        title: 'Could not save',
        message: 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveConfirm = async (): Promise<void> => {
    if (!removeTarget) return;
    setSaving(true);
    try {
      if (removeTarget.mode === 'supervisor') {
        await updateProviderSupervisor(medplum, practitionerId, null);
      } else {
        await updateSupervisorSupervisees(
          medplum,
          practitionerId,
          data.superviseeIds.filter((id) => id !== removeTarget.row.id),
        );
      }
      data.refresh();
      setRemoveTarget(null);
      notifications.show({
        color: 'green',
        title: 'Supervision updated',
        message:
          removeTarget.mode === 'supervisor'
            ? 'Supervisor removed.'
            : 'Supervisee removed.',
      });
    } catch {
      notifications.show({
        color: 'red',
        title: 'Could not save',
        message: 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRequiresCosignatureChange = async (checked: boolean): Promise<void> => {
    const previous = data.requiresCosignature;
    data.setRequiresCosignatureLocal(checked);
    setSaving(true);
    try {
      await updateRequiresCosignature(medplum, practitionerId, checked);
      notifications.show({
        color: 'green',
        title: 'Cosignature setting updated',
        message: checked
          ? 'Clinical notes will require supervisor cosignature.'
          : 'Clinical notes will not require supervisor cosignature.',
      });
    } catch {
      data.setRequiresCosignatureLocal(previous);
      notifications.show({
        color: 'red',
        title: 'Could not save',
        message: 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const supervisorRows = data.supervisorRow ? [data.supervisorRow] : [];
  const subjectName = data.practitioner ? practitionerDisplayName(data.practitioner) : 'this user';

  return (
    <>
      <Card withBorder w="100%" maw="100%" style={{ alignSelf: 'stretch' }}>
        <Stack gap="lg">
          <div>
            <Title order={4}>Supervision</Title>
          </div>

          {data.loading ? (
            <Group justify="center" py="md">
              <Loader size="sm" />
            </Group>
          ) : data.error ? (
            <Text size="sm" c="red">
              {data.error}
            </Text>
          ) : (
            <>
              {showProviderSection && (
                <>
                  <SupervisionSection
                    title="Supervisor"
                    rows={supervisorRows}
                    canEdit={canEdit}
                    emptyMessage="No supervisor assigned."
                    addLabel={supervisorRows.length > 0 ? 'Change supervisor' : 'Assign supervisor'}
                    onAdd={() => openAddModal('supervisor')}
                    onRemove={(row) => setRemoveTarget({ mode: 'supervisor', row })}
                  />
                  <Switch
                    label="Requires cosignature"
                    description="When enabled, this provider's clinical notes must be cosigned by a supervisor."
                    checked={data.requiresCosignature}
                    onChange={(event) => void handleRequiresCosignatureChange(event.currentTarget.checked)}
                    disabled={!canEdit || saving}
                  />
                </>
              )}

              {showSupervisorSection && (
                <SupervisionSection
                  title="Supervisees"
                  rows={data.superviseeRows}
                  canEdit={canEdit}
                  emptyMessage="No supervisees assigned."
                  addLabel="Add supervisee"
                  onAdd={() => openAddModal('supervisee')}
                  onRemove={(row) => setRemoveTarget({ mode: 'supervisee', row })}
                />
              )}
            </>
          )}
        </Stack>
      </Card>

      <Modal
        opened={addModalMode !== null}
        onClose={closeAddModal}
        title={addModalMode === 'supervisor' ? 'Assign supervisor' : 'Add supervisee'}
        centered
      >
        <Stack gap="md">
          <Select
            label={addModalMode === 'supervisor' ? 'Supervisor' : 'Provider'}
            placeholder={addModalMode === 'supervisor' ? 'Search supervisors…' : 'Search providers…'}
            searchable
            data={addCandidates.map((c) => ({ value: c.value, label: c.label }))}
            value={selectedCandidateId}
            onChange={setSelectedCandidateId}
            disabled={saving}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeAddModal} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleAddConfirm()} loading={saving} disabled={!selectedCandidateId}>
              {addModalMode === 'supervisor' && supervisorRows.length > 0 ? 'Save' : 'Add'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={removeTarget?.mode === 'supervisor' ? 'Remove supervisor?' : 'Remove supervisee?'}
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="gray.7">
            {removeTarget?.mode === 'supervisor'
              ? `Remove ${removeTarget.row.name} as supervisor for ${subjectName}?`
              : `Remove ${removeTarget?.row.name} as a supervisee of ${subjectName}?`}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setRemoveTarget(null)} disabled={saving}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void handleRemoveConfirm()} loading={saving}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
